import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Users, Plus, Pencil, Key, ShieldCheck, ShieldOff, Trash2, Loader2, Settings2, RotateCcw } from "lucide-react";
import { ROLE_LABELS } from "@/hooks/usePermissions";

const ROLE_COLORS = {
  super_admin: "bg-red-100 text-red-700 border-red-200",
  admin: "bg-purple-100 text-purple-700 border-purple-200",
  manager: "bg-blue-100 text-blue-700 border-blue-200",
  user: "bg-green-100 text-green-700 border-green-200",
};

const PERMISSION_GROUPS = [
  { label: "Dashboard", keys: ["dashboard"] },
  { label: "Products", keys: ["products_view", "products_edit", "products_delete"] },
  { label: "Bookings", keys: ["bookings_view", "bookings_edit"] },
  { label: "Enquiries", keys: ["enquiries_view", "enquiries_edit"] },
  { label: "Blog", keys: ["blog_view", "blog_edit"] },
  { label: "Coupons", keys: ["coupons_view", "coupons_edit"] },
  { label: "Content", keys: ["faq_edit", "tickers_edit", "pages_edit", "banners_edit"] },
  { label: "Administration", keys: ["settings", "team_management", "approvals", "categories_edit", "data_purge"] },
];

const PERM_LABELS = {
  dashboard: "View Dashboard", products_view: "View Products", products_edit: "Create/Edit Products", products_delete: "Delete Products",
  bookings_view: "View Bookings", bookings_edit: "Update Booking Status", enquiries_view: "View Enquiries", enquiries_edit: "Update Enquiries",
  coupons_view: "View Coupons", coupons_edit: "Create/Edit Coupons", blog_view: "View Blog", blog_edit: "Create/Edit Blog",
  faq_edit: "Manage FAQ", tickers_edit: "Manage Tickers", pages_edit: "Manage CMS Pages", banners_edit: "Manage Banners",
  settings: "Site Settings", team_management: "Team Management", data_purge: "Data Purge", approvals: "Vendor Approvals", categories_edit: "Manage Categories",
};

const PERMISSION_SUMMARY = {
  super_admin: "Full access + Data Purge",
  admin: "Full access + Team Management",
  manager: "Products, Bookings, Enquiries, Blog, CMS, Banners, FAQ, Tickers",
  user: "View-only: Dashboard, Products, Bookings, Enquiries, Blog",
};

function PermissionToggles({ permissions, onChange, roleDefaults }) {
  return (
    <div className="space-y-4">
      {PERMISSION_GROUPS.map((group) => (
        <div key={group.label} className="border border-border rounded-lg p-3">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{group.label}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {group.keys.map((key) => {
              const isCustom = roleDefaults && permissions[key] !== roleDefaults[key];
              return (
                <label key={key} className={`flex items-center justify-between gap-2 py-1.5 px-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors ${isCustom ? "bg-amber-50 border border-amber-200" : ""}`}>
                  <span className="text-sm">{PERM_LABELS[key] || key}</span>
                  <Switch
                    checked={!!permissions[key]}
                    onCheckedChange={(v) => onChange({ ...permissions, [key]: v })}
                    data-testid={`perm-${key}`}
                  />
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminTeam() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showPassword, setShowPassword] = useState(null);
  const [showPerms, setShowPerms] = useState(null);
  const [saving, setSaving] = useState(false);
  const [roleDefaults, setRoleDefaults] = useState({});

  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "user" });
  const [formPerms, setFormPerms] = useState({});
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", role: "" });
  const [newPassword, setNewPassword] = useState("");
  const [editPerms, setEditPerms] = useState({});

  const load = useCallback(async () => {
    try {
      const [teamRes, defaultsRes] = await Promise.all([
        api.get("/admin/team"),
        api.get("/admin/team/role-defaults"),
      ]);
      setMembers(teamRes.data);
      setRoleDefaults(defaultsRes.data.defaults || {});
    } catch { toast.error("Failed to load team"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // When role changes in create form, reset permissions to role defaults
  useEffect(() => {
    if (roleDefaults[form.role]) {
      setFormPerms({ ...roleDefaults[form.role] });
    }
  }, [form.role, roleDefaults]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      return toast.error("Name, email, and password are required");
    }
    setSaving(true);
    try {
      // Calculate custom_permissions: only send diffs from role defaults
      const defaults = roleDefaults[form.role] || {};
      const custom = {};
      for (const [k, v] of Object.entries(formPerms)) {
        if (defaults[k] !== v) custom[k] = v;
      }
      await api.post("/admin/team", { ...form, custom_permissions: Object.keys(custom).length ? custom : {} });
      toast.success(`${ROLE_LABELS[form.role] || form.role} account created`);
      setShowCreate(false);
      setForm({ name: "", email: "", phone: "", password: "", role: "user" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create account");
    } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!showEdit) return;
    setSaving(true);
    try {
      await api.put(`/admin/team/${showEdit.id}`, editForm);
      toast.success("Member updated");
      setShowEdit(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const handleToggleStatus = async (member) => {
    try {
      const { data } = await api.put(`/admin/team/${member.id}/status`, { is_active: !member.is_active });
      toast.success(data.is_active ? "Account activated" : "Account deactivated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const handleResetPassword = async () => {
    if (!showPassword || newPassword.length < 6) {
      return toast.error("Password must be at least 6 characters");
    }
    setSaving(true);
    try {
      await api.put(`/admin/team/${showPassword.id}/password`, { password: newPassword });
      toast.success("Password updated");
      setShowPassword(null);
      setNewPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset password");
    } finally { setSaving(false); }
  };

  const handleSavePerms = async () => {
    if (!showPerms) return;
    setSaving(true);
    try {
      const defaults = roleDefaults[showPerms.role] || {};
      const custom = {};
      for (const [k, v] of Object.entries(editPerms)) {
        if (defaults[k] !== v) custom[k] = v;
      }
      await api.put(`/admin/team/${showPerms.id}/permissions`, { custom_permissions: custom });
      toast.success("Permissions updated");
      setShowPerms(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update permissions");
    } finally { setSaving(false); }
  };

  const handleResetPermsToDefaults = () => {
    if (!showPerms) return;
    const defaults = roleDefaults[showPerms.role] || {};
    setEditPerms({ ...defaults });
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Delete ${member.name}'s account? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/team/${member.id}`);
      toast.success("Account deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const openEdit = (m) => {
    setEditForm({ name: m.name, email: m.email, phone: m.phone || "", role: m.role });
    setShowEdit(m);
  };

  const openPerms = (m) => {
    const defaults = roleDefaults[m.role] || {};
    const merged = { ...defaults, ...(m.custom_permissions || {}) };
    setEditPerms(merged);
    setShowPerms(m);
  };

  const hasCustomPerms = (m) => {
    return m.custom_permissions && Object.keys(m.custom_permissions).length > 0;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-team-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#E5F0F0]">
              <Users className="w-5 h-5 text-teal" />
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold">Team Management</h1>
              <p className="text-sm text-muted-foreground">{members.length} staff member{members.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="create-team-member-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Member
          </Button>
        </div>

        {/* Role Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {["super_admin", "admin", "manager", "user"].map((role) => (
            <div key={role} className="bg-white border border-border rounded-xl p-4" data-testid={`role-card-${role}`}>
              <div className="flex items-center gap-2 mb-2">
                <Badge className={`${ROLE_COLORS[role]} text-xs`}>{ROLE_LABELS[role]}</Badge>
                <span className="text-sm font-semibold">{members.filter(m => m.role === role).length}</span>
              </div>
              <p className="text-xs text-muted-foreground">{PERMISSION_SUMMARY[role]}</p>
            </div>
          ))}
        </div>

        {/* Team Table */}
        <div className="bg-white border border-border rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-teal" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id} className={m.is_active === false ? "opacity-50" : ""} data-testid={`team-row-${m.id}`}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{m.email}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{m.phone || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge className={`${ROLE_COLORS[m.role] || "bg-gray-100 text-gray-700"} text-xs`}>
                          {ROLE_LABELS[m.role] || m.role}
                        </Badge>
                        {hasCustomPerms(m) && (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Custom</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {m.is_active !== false ? (
                        <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">Active</Badge>
                      ) : (
                        <Badge className="bg-red-50 text-red-700 border-red-200 text-xs">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {m.role !== "super_admin" && (
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(m)} title="Edit" data-testid={`edit-${m.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          {m.role !== "admin" && (
                            <Button variant="ghost" size="sm" onClick={() => openPerms(m)} title="Permissions" data-testid={`perms-${m.id}`}>
                              <Settings2 className="w-3.5 h-3.5 text-blue-600" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => { setShowPassword(m); setNewPassword(""); }} title="Reset Password" data-testid={`password-${m.id}`}>
                            <Key className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleToggleStatus(m)}
                            title={m.is_active !== false ? "Deactivate" : "Activate"}
                            data-testid={`toggle-status-${m.id}`}
                          >
                            {m.is_active !== false ? <ShieldOff className="w-3.5 h-3.5 text-amber-600" /> : <ShieldCheck className="w-3.5 h-3.5 text-green-600" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(m)} title="Delete" data-testid={`delete-${m.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      )}
                      {m.role === "super_admin" && (
                        <span className="text-xs text-muted-foreground">Protected</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="create-member-dialog">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>Create a new staff account with role-based permissions</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="John Doe" data-testid="create-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="john@company.com" data-testid="create-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 98765 43210" data-testid="create-phone" />
              </div>
              <div>
                <Label>Password *</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 6 characters" data-testid="create-password" />
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="create-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager — {PERMISSION_SUMMARY.manager}</SelectItem>
                  <SelectItem value="user">User — {PERMISSION_SUMMARY.user}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Permission Toggles */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Permissions</Label>
                <span className="text-xs text-muted-foreground">Customize what this member can access</span>
              </div>
              <PermissionToggles
                permissions={formPerms}
                onChange={setFormPerms}
                roleDefaults={roleDefaults[form.role]}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="create-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!showEdit} onOpenChange={(v) => !v && setShowEdit(null)}>
        <DialogContent className="sm:max-w-[700px]" data-testid="edit-member-dialog">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>Update account details for {showEdit?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} data-testid="edit-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))} data-testid="edit-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input value={editForm.phone} onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))} data-testid="edit-phone" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(null)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={saving} className="bg-teal hover:bg-teal-dark text-white" data-testid="edit-submit">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={!!showPerms} onOpenChange={(v) => !v && setShowPerms(null)}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="permissions-dialog">
          <DialogHeader>
            <DialogTitle>Manage Permissions</DialogTitle>
            <DialogDescription>
              Customize access for <strong>{showPerms?.name}</strong> ({ROLE_LABELS[showPerms?.role] || showPerms?.role})
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-muted-foreground">Yellow-highlighted items differ from role defaults</p>
              <Button variant="outline" size="sm" onClick={handleResetPermsToDefaults} data-testid="reset-perms-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to Defaults
              </Button>
            </div>
            <PermissionToggles
              permissions={editPerms}
              onChange={setEditPerms}
              roleDefaults={roleDefaults[showPerms?.role]}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPerms(null)}>Cancel</Button>
            <Button onClick={handleSavePerms} disabled={saving} className="bg-teal hover:bg-teal-dark text-white" data-testid="save-perms-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showPassword} onOpenChange={(v) => !v && setShowPassword(null)}>
        <DialogContent className="sm:max-w-[400px]" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Set a new password for this team member</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">
            Set a new password for <strong>{showPassword?.name}</strong> ({showPassword?.email})
          </p>
          <div>
            <Label>New Password *</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" data-testid="new-password" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPassword(null)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={saving} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="password-submit">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
